import {
  kontiIzVnosa,
  vnosIzKontov,
  type IzbiraNadrejene,
  type PostavkaZaUrejanje,
  type SkupinaZaUrejanje,
} from '@koda/shared';
import { useState, type JSX } from 'react';

/** Ali je šifra trimestna in se zato išče po tretji ravni. */
function jeTrimesten(konto: string): boolean {
  return konto.length === 3;
}

/**
 * Šifre, ki v tej tabeli ne bodo prijele.
 *
 * Postavka se za dani dvomestni konto išče po drugi ali tretji ravni; to je
 * nastavitev sheme. Trimestna šifra pri predponi, ki ni nastavljena na tretjo
 * raven, tiho ne prime — zato na to opozorimo ob vnosu, ne šele ob razvrstitvi
 * (docs/ARHITEKTURA.md, 4.4).
 */
export function sporneSifre(konti: readonly string[], trimestni: readonly string[]): string[] {
  return konti.filter((k) => jeTrimesten(k) && !trimestni.includes(k.slice(0, 2)));
}

/** Oznaka ob vrstici, ki pri tem podjetju odstopa od standardne sheme. */
function Oznaka({ naPonastavi }: { naPonastavi: () => void }): JSX.Element {
  return (
    <span className="prilagojeno">
      prilagojeno
      <button type="button" className="povezava" onClick={naPonastavi}>
        Ponastavi
      </button>
    </span>
  );
}

/**
 * Tabela skupin izkaza z razponi kontov.
 *
 * Oblika je enaka preglednici, iz katere se razvrščanje ureja: vrstica je
 * skupina, razpon pa dve številki. Zadnja vrstica je prazna, za novo skupino.
 */
export function TabelaSkupin({
  skupine,
  nadskupine,
  naShrani,
  naPonastavi,
  naDodaj,
}: {
  skupine: SkupinaZaUrejanje[];
  nadskupine: IzbiraNadrejene[];
  naShrani: (id: string, sprememba: { od: number; do: number }) => void;
  naPonastavi: (id: string) => void;
  naDodaj: (skupina: { naziv: string; nadskupina: string; od: number; do: number }) => void;
}): JSX.Element {
  const [osnutki, setOsnutki] = useState<Record<string, { od: string; do: string }>>({});
  const [nov, setNov] = useState({ naziv: '', nadskupina: '', od: '', do: '' });
  const novaVeljavna =
    nov.naziv.trim() !== '' && nov.nadskupina !== '' && /^\d+$/.test(nov.od) && /^\d+$/.test(nov.do);

  const osnutek = (s: SkupinaZaUrejanje): { od: string; do: string } =>
    osnutki[s.id] ?? { od: s.od === null ? '' : String(s.od), do: s.do === null ? '' : String(s.do) };
  const spremenjena = (s: SkupinaZaUrejanje): boolean => {
    const o = osnutki[s.id];
    return o !== undefined && (o.od !== String(s.od ?? '') || o.do !== String(s.do ?? ''));
  };
  const nastavi = (id: string, del: Partial<{ od: string; do: string }>): void => {
    setOsnutki((prej) => {
      const s = skupine.find((x) => x.id === id)!;
      const zdaj = prej[id] ?? { od: String(s.od ?? ''), do: String(s.do ?? '') };
      return { ...prej, [id]: { ...zdaj, ...del } };
    });
  };
  const pozabi = (id: string): void => {
    setOsnutki((prej) => {
      const brez = { ...prej };
      delete brez[id];
      return brez;
    });
  };

  return (
    <table className="nastavitve">
      <thead>
        <tr>
          <th className="ozek">izkaz</th>
          <th>skupina</th>
          <th className="ozek stevilka">od</th>
          <th className="ozek stevilka">do</th>
          <th>nadskupina</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {skupine.map((s) => (
          <tr key={s.id} className={spremenjena(s) ? 'spremenjena' : ''}>
            <td>{s.izkaz}</td>
            <td>{s.naziv}</td>
            <td className="stevilka">
              <input
                className="razpon"
                inputMode="numeric"
                value={osnutek(s).od}
                onChange={(d) => {
                  nastavi(s.id, { od: d.target.value });
                }}
              />
            </td>
            <td className="stevilka">
              <input
                className="razpon"
                inputMode="numeric"
                value={osnutek(s).do}
                onChange={(d) => {
                  nastavi(s.id, { do: d.target.value });
                }}
              />
            </td>
            <td className="drobno">{s.nadskupina}</td>
            <td className="ukrepi">
              {spremenjena(s) ? (
                <>
                  <button
                    type="button"
                    className="gumb drobni"
                    onClick={() => {
                      const o = osnutek(s);
                      naShrani(s.id, {
                        od: Number.parseInt(o.od, 10),
                        do: Number.parseInt(o.do, 10),
                      });
                      pozabi(s.id);
                    }}
                  >
                    Shrani
                  </button>
                  <button
                    type="button"
                    className="povezava"
                    onClick={() => {
                      pozabi(s.id);
                    }}
                  >
                    Prekliči
                  </button>
                </>
              ) : (
                s.prilagojeno && (
                  <Oznaka
                    naPonastavi={() => {
                      naPonastavi(s.id);
                    }}
                  />
                )
              )}
            </td>
          </tr>
        ))}

        {/* Prazna vrstica za novo skupino: razpona, ki ga standardna shema ne
            pokriva, ni mogoce dodati z raztegom sosednje - ta bi jo povozil. */}
        <tr className="nova">
          <td className="drobno">nova</td>
          <td>
            <input
              value={nov.naziv}
              placeholder="nova skupina"
              onChange={(d) => {
                setNov({ ...nov, naziv: d.target.value });
              }}
            />
          </td>
          <td className="stevilka">
            <input
              className="razpon"
              inputMode="numeric"
              value={nov.od}
              onChange={(d) => {
                setNov({ ...nov, od: d.target.value });
              }}
            />
          </td>
          <td className="stevilka">
            <input
              className="razpon"
              inputMode="numeric"
              value={nov.do}
              onChange={(d) => {
                setNov({ ...nov, do: d.target.value });
              }}
            />
          </td>
          <td>
            <select
              value={nov.nadskupina}
              onChange={(d) => {
                setNov({ ...nov, nadskupina: d.target.value });
              }}
            >
              <option value="">izberi …</option>
              {nadskupine.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.nadrejena} · {n.naziv}
                </option>
              ))}
            </select>
          </td>
          <td className="ukrepi">
            <button
              type="button"
              className="gumb drobni"
              disabled={!novaVeljavna}
              onClick={() => {
                naDodaj({
                  naziv: nov.naziv.trim(),
                  nadskupina: nov.nadskupina,
                  od: Number.parseInt(nov.od, 10),
                  do: Number.parseInt(nov.do, 10),
                });
                setNov({ naziv: '', nadskupina: '', od: '', do: '' });
              }}
            >
              Dodaj
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/**
 * Tabela postavk (izkaza ali denarnega toka) s konti v enem polju.
 *
 * Konti se ločijo s podpičjem, tako kot v preglednici. Vrstice, ki seštevajo
 * skupine in ne kontov, so označene in jih ni mogoče urejati.
 */
export function TabelaPostavk({
  postavke,
  nadrejene,
  trimestni,
  nazivNadrejene,
  naShrani,
  naPonastavi,
  naDodaj,
}: {
  postavke: PostavkaZaUrejanje[];
  nadrejene: IzbiraNadrejene[];
  /** Dvomestne predpone, pri katerih se išče po trimestnem kontu. */
  trimestni: string[];
  nazivNadrejene: string;
  naShrani: (id: string, konti: string[]) => void;
  naPonastavi: (id: string) => void;
  naDodaj: (postavka: { naziv: string; nadrejena: string; konti: string[] }) => void;
}): JSX.Element {
  const [osnutki, setOsnutki] = useState<Record<string, string>>({});
  const [novNaziv, setNovNaziv] = useState('');
  const [novaNadrejena, setNovaNadrejena] = useState('');
  const [novKonti, setNovKonti] = useState('');

  const osnutek = (p: PostavkaZaUrejanje): string => osnutki[p.id] ?? vnosIzKontov(p.konti);
  const spremenjena = (p: PostavkaZaUrejanje): boolean =>
    osnutki[p.id] !== undefined && osnutki[p.id] !== vnosIzKontov(p.konti);
  const pozabi = (id: string): void => {
    setOsnutki((prej) => {
      const brez = { ...prej };
      delete brez[id];
      return brez;
    });
  };

  return (
    <table className="nastavitve">
      <thead>
        <tr>
          <th>postavka</th>
          <th>{nazivNadrejene}</th>
          <th>konti</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {postavke.map((p) => {
          const vpisani = kontiIzVnosa(osnutek(p));
          const sporni = sporneSifre(vpisani, trimestni);
          return (
            <tr key={p.id} className={spremenjena(p) ? 'spremenjena' : ''}>
              <td>{p.naziv}</td>
              <td className="drobno">{p.skupina}</td>
              <td>
                {p.izSkupin === true ? (
                  <span className="drobno">sešteva skupine izkaza, ne kontov</span>
                ) : (
                  <>
                    <input
                      className="konti"
                      value={osnutek(p)}
                      onChange={(d) => {
                        setOsnutki((prej) => ({ ...prej, [p.id]: d.target.value }));
                      }}
                    />
                    {sporni.length > 0 && (
                      <p className="napaka drobno">
                        {sporni.join(', ')}: trimestna šifra pri predponi, ki se išče dvomestno —
                        pravilo ne bo prijelo.
                      </p>
                    )}
                  </>
                )}
              </td>
              <td className="ukrepi">
                {spremenjena(p) ? (
                  <>
                    <button
                      type="button"
                      className="gumb drobni"
                      onClick={() => {
                        naShrani(p.id, vpisani);
                        pozabi(p.id);
                      }}
                    >
                      Shrani
                    </button>
                    <button
                      type="button"
                      className="povezava"
                      onClick={() => {
                        pozabi(p.id);
                      }}
                    >
                      Prekliči
                    </button>
                  </>
                ) : (
                  p.prilagojeno && (
                    <Oznaka
                      naPonastavi={() => {
                        naPonastavi(p.id);
                      }}
                    />
                  )
                )}
              </td>
            </tr>
          );
        })}

        {/* Prazna vrstica za novo postavko, kot v preglednici. */}
        <tr className="nova">
          <td>
            <input
              value={novNaziv}
              placeholder="nova postavka"
              onChange={(d) => {
                setNovNaziv(d.target.value);
              }}
            />
          </td>
          <td>
            <select
              value={novaNadrejena}
              onChange={(d) => {
                setNovaNadrejena(d.target.value);
              }}
            >
              <option value="">izberi …</option>
              {nadrejene.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.naziv}
                </option>
              ))}
            </select>
          </td>
          <td>
            <input
              className="konti"
              value={novKonti}
              placeholder="npr. 760; 762"
              onChange={(d) => {
                setNovKonti(d.target.value);
              }}
            />
          </td>
          <td className="ukrepi">
            <button
              type="button"
              className="gumb drobni"
              disabled={novNaziv.trim() === '' || novaNadrejena === ''}
              onClick={() => {
                naDodaj({
                  naziv: novNaziv.trim(),
                  nadrejena: novaNadrejena,
                  konti: kontiIzVnosa(novKonti),
                });
                setNovNaziv('');
                setNovaNadrejena('');
                setNovKonti('');
              }}
            >
              Dodaj
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  );
}
